import type { JSX } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { VolumeUp } from '@mui/icons-material';
import { AppBar, Tab, Tabs } from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    ServerRequestState,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';

export function CloudConnectorSmartCall() {
    const { zoneId } = useParams();

    if (!zoneId) {
        return null;
    }

    return (
        <OperatorRoutedModal
            variant={ModalVariant.BIG}
            render={controller => (
                <SmartCallProfilesOutletWrapper
                    zoneId={zoneId}
                    onClose={controller.close}
                    navigateWithinModal={controller.navigateWithinModal}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                />
            )}
        />
    );
}

function SmartCallProfilesOutletWrapper({
    zoneId,
    onClose,
    navigateWithinModal,
    onDirtyStateChange,
}: {
    zoneId: string;
    onClose: () => void;
    navigateWithinModal: (url: string) => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
}) {
    const [profilesState, refetchProfiles] = useServerFetch<
        SmartCallProfile[],
        { zoneId: string },
        null
    >(
        ({ zoneId }) => ({
            url: `/ui-api/operator-account/cloud-connector/smart-call/zone/${zoneId}/profiles`,
        }),
        { zoneId: zoneId },
    );
    return (
        <SmartCallOutlet
            context={{
                zoneId: zoneId,
                onClose: onClose,
                navigateWithinModal: navigateWithinModal,
                profilesState: profilesState,
                refetchProfiles: refetchProfiles,
                onDirtyStateChange: onDirtyStateChange,
            }}
        />
    );
}

export interface SmartCallProfile {
    id: string;
    description: string;
    createdAt: string;
    lastChangedAt: string | null;
    isDefault: boolean;
    isDeletable: boolean;
}

export function useSmartCallOutletContext() {
    return useOutletContext<SmartCallContext>();
}

function SmartCallOutlet({ context }: { context: SmartCallContext }) {
    return <Outlet context={context} />;
}

export interface SmartCallContext {
    zoneId: string;
    onClose: () => void;
    navigateWithinModal: (url: string) => void;
    profilesState: ServerRequestState<SmartCallProfile[], null>;
    refetchProfiles: () => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
}

export function SmartCallModalHeader({
    buttons,
    editMode,
}: {
    buttons: JSX.Element;
    editMode: boolean;
}) {
    return (
        <HeaderComponent
            title={'SmartCall'}
            Icon={VolumeUp}
            buttons={buttons}
            editMode={editMode}
        />
    );
}

export enum SmartCallTab {
    PROFILES = 'PROFILES',
    WEEKLY_PLAN = 'WEEKLY_PLAN',
    INDIVIDUAL_DATES = 'INDIVIDUAL_DATES',
}

export function SmartCallTabs({
    smartCallTab,
    navigateWithinModal,
}: {
    smartCallTab: SmartCallTab;
    navigateWithinModal: (url: string) => void;
}) {
    return (
        <AppBar position="static" elevation={0}>
            <Tabs
                value={smartCallTab}
                onChange={(_e, value) => {
                    switch (value as SmartCallTab) {
                        case SmartCallTab.PROFILES:
                            navigateWithinModal('profiles');
                            break;
                        case SmartCallTab.WEEKLY_PLAN:
                            navigateWithinModal('weekly-plan');
                            break;
                        case SmartCallTab.INDIVIDUAL_DATES:
                            navigateWithinModal('individual-dates');
                            break;
                    }
                }}
                indicatorColor="secondary"
                textColor="inherit"
            >
                <Tab
                    style={{
                        marginLeft: 24,
                    }}
                    label={
                        <Localized
                            de="Profil"
                            fr="Profil"
                            it="Profilo"
                            en="Profile"
                        />
                    }
                    value={SmartCallTab.PROFILES}
                />
                <Tab
                    label={
                        <Localized
                            de="Wochenplan"
                            fr="Planning hebdomadaire"
                            it="Piano settimanale"
                            en="Weekly Plan"
                        />
                    }
                    value={SmartCallTab.WEEKLY_PLAN}
                />
                <Tab
                    label={
                        <Localized
                            de="Einzeltage"
                            fr="Jours uniques"
                            it="Giorni singoli"
                            en="Individual days"
                        />
                    }
                    value={SmartCallTab.INDIVIDUAL_DATES}
                />
            </Tabs>
        </AppBar>
    );
}
